// Libraries
import { Box, Flex, Tag, Text } from '@chakra-ui/react';
import { format } from 'date-fns';
import { ReactElement } from 'react';
// Components
import { RatingLabeled } from '@molecules/index';
import { HField } from './Components';
// Utils
import text from '@translations/en.json';
import { formatNumberToDecimal, stringDateToTime } from '@utils/helpers';
// Types
import { VoyageCardProps } from './VoyageCard.type';

/**
 * VoyageCard component.
 *
 * @component
 * @param {VoyageCardProps} props - The props for the VoyageCard component.
 * @param {Voyage} props.voyage - The voyage object containing voyage details.
 * @param {boolean} props.isActive - Indicates whether the voyage has the active tag.
 * @returns {ReactElement} The rendered VoyageCard component.
 */
export const VoyageCard = ({ voyage, isActive }: VoyageCardProps): ReactElement => {
  const timeDeparture = format(stringDateToTime(voyage.timeDeparture as string), 'MMM dd, HH:mm');
  const timeArrival = format(stringDateToTime(voyage.timeArrival as string), 'MMM dd, HH:mm');

  return (
    <Box px={4} py={3} w='full'>
      {isActive && <Tag>{text.tag_active}</Tag>}
      <Flex justify='space-between'>
        <Text color='grey.800' fontSize='s' fontWeight='medium' flex={1}>
          {`${voyage.originPort} - ${voyage.destinationPort}`}
        </Text>
        <RatingLabeled rating={voyage.ciiRating ?? 'N'} score={voyage.ciiScore} />
      </Flex>
      <Box mt={1}>
        <Text fontSize='s' color='grey.100'>
          {` ${timeDeparture} UTC - ${timeArrival} UTC`}
        </Text>
      </Box>
      <Box mt={8}></Box>
      <Flex justify='space-between'>
        <HField name={text.voyage_card_distance} value={`${formatNumberToDecimal(voyage.distanceTraveled, 0)} NM`} />
        <HField name={text.total_foc} value={`${formatNumberToDecimal(voyage.fuelOilConsumption, 1)} MT`} />
        <HField name={text.voyage_card_avg_speed} value={`${formatNumberToDecimal(voyage.averageSpeed, 1)} kn`} />
      </Flex>
    </Box>
  );
};
